import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Clock,
  Dumbbell,
  HeartHandshake,
  Lock,
  PlayCircle,
  Salad,
  ShieldCheck,
  Sparkles,
  Star,
  Video,
  Wallet,
  Zap,
} from "lucide-react";

const FULL_PRICE = 300000;
const CAMPAIGN_PRICE = 250000;

export default function CampaignOfferPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

const form =
  location?.state?.form ||
  safeJsonParse(localStorage.getItem("campaignWizardForm"));

const analysis =
  location?.state?.analysis ||
  safeJsonParse(localStorage.getItem("campaignAnalysis"));

const preview = location?.state?.preview || null;

const mobileNumber =
  location?.state?.mobileNumber ||
  localStorage.getItem("campaignMobileNumber") ||
  "";

const planGuid =
  location?.state?.planGuid ||
  localStorage.getItem("campaignPlanGuid") ||
  "";

const token =
  location?.state?.token ||
  localStorage.getItem("campaignToken") ||
  "";

  const saving = FULL_PRICE - CAMPAIGN_PRICE;

  const planSummary = useMemo(() => {
    return {
      goal: preview?.goalTitle || analysis?.goalTitle || "برنامه اختصاصی",
      place: preview?.placeTitle || "بر اساس شرایط شما",
      level: preview?.levelTitle || "شخصی‌ سازی‌ شده",
    };
  }, [preview, analysis]);

const activatePlan = () => {
  navigate("/body-analysis/payment-preparing", {
    state: {
      form,
      analysis,
      preview,
      mobileNumber,
      planGuid,
      token,
    },
  });
};

  return (
    <main dir="rtl" className="font-vazir relative min-h-screen overflow-hidden bg-[#030712] text-white">
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(16,185,129,.34),transparent_34%),radial-gradient(circle_at_0%_85%,rgba(37,99,235,.28),transparent_38%),radial-gradient(circle_at_100%_70%,rgba(34,211,238,.18),transparent_30%)]" />
      <div className="fixed inset-0 opacity-[.15] bg-[linear-gradient(rgba(255,255,255,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.08)_1px,transparent_1px)] bg-[size:34px_34px]" />
      <div className="fixed inset-0 bg-gradient-to-b from-transparent via-[#030712]/35 to-[#030712]" />

      <section className="relative z-10 mx-auto flex h-screen max-w-md flex-col px-4 pt-5 pb-[104px]">
        <header className="flex items-center justify-between">
          <img src="/logo.png" alt="MOGYM" className="h-10 w-auto" />

          <div className="rounded-full border border-amber-300/20 bg-amber-300/10 px-3 py-2 text-[11px] text-amber-100 backdrop-blur-2xl">
            پیشنهاد فعالسازی
          </div>
        </header>

        <div className="mt-5 min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1 pb-4 scrollbar-hide">
          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className="rounded-[34px] border border-emerald-400/20 bg-emerald-400/10 p-5 text-center shadow-[0_0_70px_rgba(52,211,153,.16)] backdrop-blur-2xl"
          >
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-[26px] bg-emerald-300 text-black shadow-[0_0_60px_rgba(52,211,153,.48)]">
              <Sparkles size={32} />
            </div>

            <h1 className="mt-5 text-3xl font-black leading-tight">
              برنامه اختصاصی تو
              <span className="mt-2 block bg-gradient-to-l from-emerald-300 via-cyan-300 to-blue-400 bg-clip-text text-transparent">
                آماده فعالسازی
              </span>
            </h1>

            <p className="mt-4 text-sm leading-7 text-white/62">
           پیش‌ نمایش رو دیدی؛ نسخه کامل شامل برنامه تمرین، تغذیه، ویدئوها و امکان اصلاح ۲۴ ساعته
            </p>

            <div className="mt-5 grid grid-cols-3 gap-2">
              <Mini label="هدف" value={planSummary.goal} />
              <Mini label="محل تمرین" value={planSummary.place} />
              <Mini label="سطح" value={planSummary.level} />
            </div>
          </motion.div>

          <div className="mt-5 rounded-[34px] border border-white/10 bg-white/[.055] p-5 shadow-2xl backdrop-blur-2xl">
            <div className="flex items-center gap-2 text-emerald-200">
              <Star size={20} />
              <h2 className="font-black">بعد از فعالسازی چه چیزهایی میگیری؟</h2>
            </div>

            <div className="mt-5 space-y-3">
              <Benefit icon={Dumbbell} title="برنامه تمرین کامل" desc="روزهای تمرین، حرکات، ست، تکرار و ترتیب اجرا" />
              <Benefit icon={Salad} title="رژیم غذایی کامل" desc="وعده‌ها، میان‌وعده‌ها و پیشنهادهای غذایی" />
              <Benefit icon={Video} title="ویدئو حرکات" desc="نمایش تصویری برای اجرای درست تمرین‌ها" />
              <Benefit icon={HeartHandshake} title="اصلاح تا ۲۴ ساعت" desc="اگر برنامه با شرایطت جور نبود، درخواست اصلاح می‌دی" />
            </div>
          </div>

          <div className="mt-5 rounded-[34px] border border-amber-300/20 bg-amber-300/10 p-5 backdrop-blur-2xl">
            <div className="flex items-center gap-2 text-amber-200">
              <Clock size={20} />
              <h2 className="font-black">برنامت ذخیره شده</h2>
            </div>

            <p className="mt-3 text-sm leading-7 text-white/62">
              این تحلیل و پیش‌ نمایش برای شماره موبایل تو ذخیره میشه و میتونی نسخه کامل رو فعال کنی
            </p>

            {/* <div className="mt-4 flex items-center justify-between rounded-2xl border border-white/10 bg-black/25 px-4 py-3">
              <span className="text-xs text-white/50">کد برنامه</span>
              <span className="font-black text-amber-200">MG-{generateCode(mobileNumber)}</span>
            </div> */}
          </div>

          <div className="mt-5 rounded-[36px] border border-emerald-300/25 bg-black/35 p-5 shadow-[0_0_90px_rgba(52,211,153,.18)] backdrop-blur-2xl">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-white/45">قیمت نسخه کامل</div>
                <div className="mt-2 text-lg font-black text-white/45 line-through">
                  {formatMoney(FULL_PRICE)} تومان
                </div>
              </div>

              <div className="rounded-2xl bg-emerald-300 px-3 py-2 text-xs font-black text-black">
                {formatMoney(saving)} تومان تخفیف
              </div>
            </div>

            <div className="mt-5">
              <div className="text-xs text-emerald-200">قیمت کمپین</div>
              <div className="mt-1 flex items-end gap-2">
                <span className="text-4xl font-black text-white">
                  {formatMoney(CAMPAIGN_PRICE)}
                </span>
                <span className="pb-1 text-sm text-white/55">تومان</span>
              </div>
            </div>

            <div className="mt-5 space-y-2">
              <Trust icon={ShieldCheck} text="پرداخت امن" />
              <Trust icon={Lock} text="دسترسی کامل بعد از فعالسازی" />
              <Trust icon={PlayCircle} text="قابل مشاهده در اپلیکیشن موجیم" />
            </div>
          </div>
        </div>
      </section>

      <div className="fixed bottom-4 left-0 right-0 z-40 px-4">
        <div className="mx-auto max-w-md rounded-[26px] border border-white/10 bg-black/50 p-3 shadow-2xl backdrop-blur-2xl">
          <button
            onClick={activatePlan}
            disabled={loading}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-300 via-green-400 to-cyan-400 font-black text-black shadow-[0_0_65px_rgba(52,211,153,.52)] disabled:opacity-60"
          >
                        <ArrowRight size={19} />

            {loading ? "در حال انتقال..." : "فعالسازی برنامه من"}
          </button>
        </div>
      </div>
    </main>
  );
}

function Benefit({ icon: Icon, title, desc }) {
  return (
    <div className="flex gap-3 rounded-[24px] border border-white/10 bg-black/25 p-4">
      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-emerald-400/10 text-emerald-300">
        <Icon size={22} />
      </div>

      <div>
        <div className="font-black">{title}</div>
        <div className="mt-1 text-xs leading-6 text-white/55">{desc}</div>
      </div>
    </div>
  );
}

function Trust({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2 text-sm text-white/70">
      <Check size={16} className="text-emerald-300" />
      <Icon size={16} className="text-white/45" />
      {text}
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/25 p-3 text-center">
      <div className="text-[10px] text-white/40">{label}</div>
      <div className="mt-1 truncate text-xs font-black text-white">{value}</div>
    </div>
  );
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString("fa-IR");
}

function generateCode(mobile) {
  if (!mobile) return "8472";
  const last = String(mobile).slice(-4);
  return last || "8472";
}

function safeJsonParse(value) {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}